import { useState, useEffect, useRef } from 'react';
import {
  Box, Card, Typography, FormControl, InputLabel, Select, MenuItem,
  Button, Chip, Stack, CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { HRApiWrapper } from 'api/HRApiWrapper';

const CvConfigurationPage: React.FC = () => {
  const api: Api = useApi();
  const { invoiceApi } = api;
  const hrApi = new HRApiWrapper(api.employeeApi);
  const toast = useToastMessageQueue();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors] = useState({});

  const [corporateEntities, setCorporateEntities] = useState<any[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | ''>('');
  const [templateInfo, setTemplateInfo] = useState<{ exists: boolean; key: string; lastModified?: string } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await invoiceApi.apiVversionCorporateEntitySearchGet('1', '', 1, 100);
        setCorporateEntities(res.data?.data?.queryResult ?? []);
      } catch (e) {
        handleAPIError(e, toast, errors);
      } finally {
        setLoadingEntities(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedEntityId) { setTemplateInfo(null); return; }
    setLoadingTemplate(true);
    setTemplateInfo(null);
    hrApi.getCvTemplateInfo('1', Number(selectedEntityId))
      .then((res) => setTemplateInfo(res.data?.data ?? null))
      .catch((e) => handleAPIError(e, toast, errors))
      .finally(() => setLoadingTemplate(false));
  }, [selectedEntityId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedEntityId || !selectedFile) return;
    setUploading(true);
    try {
      await hrApi.uploadCvTemplate('1', Number(selectedEntityId), selectedFile);
      toast.success({ body: intl.get('hr.cv.config.uploaded') || 'Template uploaded successfully' });
      setSelectedFile(null);
      // Refresh template info
      const res = await hrApi.getCvTemplateInfo('1', Number(selectedEntityId));
      setTemplateInfo(res.data?.data ?? null);
    } catch (e) {
      handleAPIError(e, toast, errors);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 3 }}>
          {intl.get('hr.cv.config.title') || 'CV Template Configuration'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {intl.get('hr.cv.config.help') || 'Upload an HTML template per company. Use {{tokens}} in the template to inject employee and profile data.'}
        </Typography>

        <FormControl sx={{ minWidth: 320, mb: 3 }} disabled={loadingEntities}>
          <InputLabel>{intl.get('hr.cv.config.selectCompany') || 'Select company'}</InputLabel>
          <Select
            value={selectedEntityId}
            onChange={(e) => setSelectedEntityId(e.target.value as number)}
            label={intl.get('hr.cv.config.selectCompany') || 'Select company'}
          >
            {corporateEntities.map((ce: any) => (
              <MenuItem key={ce.id} value={ce.id}>{ce.name ?? ce.entityName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {Number(selectedEntityId) > 0 && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {intl.get('hr.cv.config.templateSection') || 'CV Template'}
            </Typography>

            {loadingTemplate ? (
              <CircularProgress size={20} />
            ) : templateInfo?.exists ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="success.main">
                  {intl.get('hr.cv.config.templateExists') || 'Template uploaded'}
                </Typography>
                {templateInfo.lastModified && (
                  <Chip
                    label={new Date(templateInfo.lastModified).toLocaleDateString()}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {intl.get('hr.cv.config.noTemplate') || 'No template uploaded yet for this company.'}
              </Typography>
            )}

            <Stack direction="row" spacing={2} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,text/html"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {selectedFile ? selectedFile.name : (intl.get('hr.cv.config.chooseFile') || 'Choose HTML file')}
              </Button>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {intl.get('hr.cv.config.upload') || 'Upload template'}
              </Button>
            </Stack>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {intl.get('hr.cv.config.tokens') || 'Available tokens'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  '{{employee.firstName}}', '{{employee.lastName}}', '{{employee.email}}', '{{employee.photo}}',
                  '{{profile.role}}', '{{profile.seniority}}', '{{profile.description}}',
                  '{{skills.technical}}', '{{skills.soft}}', '{{languages}}',
                  '{{work.experience}}', '{{certifications}}', '{{training.courses}}',
                ].map((token) => (
                  <Chip key={token} label={token} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                ))}
              </Box>
            </Box>
          </Card>
        )}
      </Box>
    </Card>
  );
};

export default CvConfigurationPage;
